import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Building,
  Users,
  Globe,
  Network,
  Shield,
  Zap,
  TrendingUp,
  CheckCircle,
  ArrowRight,
  MapPin,
  Home,
  Heart,
  Sparkles,
  Crown,
  Target,
  Rocket,
  Award,
  Star,
  ChevronRight,
} from 'lucide-react';

const SalesPage = () => {
  return (
    <div className="min-h-screen bg-linear-to-br from-slate-50 via-blue-50 to-indigo-50">
      {/* Navigation Header */}
      <header className="sticky top-0 z-50 border-b bg-white/95 backdrop-blur-sm supports-backdrop-filter:bg-white/60">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <Building className="h-8 w-8 text-blue-600" />
              <span className="text-2xl font-bold bg-linear-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
                Community App
              </span>
            </div>
            <nav className="hidden md:flex items-center space-x-8">
              <Link
                to="/pricing"
                className="text-slate-600 hover:text-blue-600 font-medium transition-colors"
              >
                Pricing
              </Link>
              <Link
                to="/licensing"
                className="text-slate-600 hover:text-blue-600 font-medium transition-colors"
              >
                Licensing
              </Link>
              <Button className="bg-linear-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700">
                Get Started
              </Button>
            </nav>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative py-20 overflow-hidden">
        <div className="absolute inset-0 bg-linear-to-r from-blue-600/10 to-indigo-600/10" />
        <div className="relative container mx-auto px-4 text-center">
          <Badge className="mb-6 bg-blue-100 text-blue-800 border-blue-200">
            <Sparkles className="h-4 w-4 mr-2" />
            The Future of Community Management
          </Badge>

          <h1 className="text-5xl md:text-7xl font-bold mb-8 bg-linear-to-r from-slate-900 via-blue-900 to-indigo-900 bg-clip-text text-transparent leading-tight">
            Transform Your Community Into a
            <span className="block bg-linear-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
              Connected Ecosystem
            </span>
          </h1>

          <p className="text-xl md:text-2xl text-slate-600 mb-12 max-w-4xl mx-auto leading-relaxed">
            Break free from fragmented systems. Join the global network that connects
            <strong className="text-slate-800">
              {' '}
              communities, citizens, land, homes, and municipalities
            </strong>
            into one powerful, intelligent ecosystem.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-16">
            <Link to="/onboarding">
              <Button
                size="lg"
                className="bg-linear-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-lg px-8 py-4"
              >
                <Rocket className="h-5 w-5 mr-2" />
                Start Your Transformation
              </Button>
            </Link>
            <Link to="/pricing">
              <Button
                variant="outline"
                size="lg"
                className="text-lg px-8 py-4 border-slate-300 hover:border-blue-600 hover:text-blue-600"
              >
                View Pricing <ArrowRight className="h-5 w-5 ml-2" />
              </Button>
            </Link>
          </div>

          {/* Hero Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 max-w-3xl mx-auto">
            <div className="text-center">
              <div className="text-3xl font-bold text-blue-600">500+</div>
              <div className="text-sm text-slate-600">Communities Ready</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-indigo-600">98%</div>
              <div className="text-sm text-slate-600">Efficiency Increase</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-green-600">$2M+</div>
              <div className="text-sm text-slate-600">Cost Savings</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-purple-600">24/7</div>
              <div className="text-sm text-slate-600">Network Operation</div>
            </div>
          </div>
        </div>
      </section>

      {/* The Problem Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-6 text-slate-900">
              Why Communities Are <span className="text-red-600">Struggling</span>
            </h2>
            <p className="text-xl text-slate-600 leading-relaxed">
              Traditional community management is broken. Isolated systems, manual processes, and
              disconnected stakeholders create inefficiency, frustration, and missed opportunities.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 mb-16">
            <Card className="border-red-200 bg-red-50/50">
              <CardHeader>
                <div className="h-12 w-12 bg-red-100 rounded-lg flex items-center justify-center mb-4">
                  <Target className="h-6 w-6 text-red-600" />
                </div>
                <CardTitle className="text-red-900">Fragmented Systems</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-red-700">
                  Multiple disconnected tools for governance, finance, maintenance, and
                  communication. Data silos prevent holistic community management.
                </p>
              </CardContent>
            </Card>

            <Card className="border-orange-200 bg-orange-50/50">
              <CardHeader>
                <div className="h-12 w-12 bg-orange-100 rounded-lg flex items-center justify-center mb-4">
                  <Users className="h-6 w-6 text-orange-600" />
                </div>
                <CardTitle className="text-orange-900">Isolated Communities</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-orange-700">
                  Communities operate in isolation, missing opportunities for shared resources, bulk
                  purchasing, and collaborative problem-solving.
                </p>
              </CardContent>
            </Card>

            <Card className="border-yellow-200 bg-yellow-50/50">
              <CardHeader>
                <div className="h-12 w-12 bg-yellow-100 rounded-lg flex items-center justify-center mb-4">
                  <TrendingUp className="h-6 w-6 text-yellow-600" />
                </div>
                <CardTitle className="text-yellow-900">Rising Costs</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-yellow-700">
                  Administrative overhead, vendor management inefficiencies, and lack of economies
                  of scale drive up operational costs unnecessarily.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* The Solution Section */}
      <section className="py-20 bg-linear-to-br from-blue-600 to-indigo-700 text-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center mb-16">
            <Badge className="mb-6 bg-white/20 text-white border-white/30">
              <Crown className="h-4 w-4 mr-2" />
              The Ultimate Solution
            </Badge>
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              Introducing the Global Community Network
            </h2>
            <p className="text-xl text-blue-100 leading-relaxed">
              A revolutionary ecosystem that connects every aspect of community life - from
              governance and finance to maintenance and social activities - while linking
              communities worldwide for unprecedented collaboration and efficiency.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="h-16 w-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <Network className="h-8 w-8 text-white" />
              </div>
              <h3 className="text-xl font-semibold mb-2">Global Network</h3>
              <p className="text-blue-100">
                Connect with communities worldwide for shared resources and collaborative
                problem-solving.
              </p>
            </div>

            <div className="text-center">
              <div className="h-16 w-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <Zap className="h-8 w-8 text-white" />
              </div>
              <h3 className="text-xl font-semibold mb-2">Unified Platform</h3>
              <p className="text-blue-100">
                One integrated system for governance, finance, maintenance, communication, and
                social activities.
              </p>
            </div>

            <div className="text-center">
              <div className="h-16 w-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <Shield className="h-8 w-8 text-white" />
              </div>
              <h3 className="text-xl font-semibold mb-2">Enterprise Security</h3>
              <p className="text-blue-100">
                Bank-level security with role-based access and comprehensive audit trails.
              </p>
            </div>

            <div className="text-center">
              <div className="h-16 w-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <TrendingUp className="h-8 w-8 text-white" />
              </div>
              <h3 className="text-xl font-semibold mb-2">Smart Analytics</h3>
              <p className="text-blue-100">
                AI-powered insights and predictive analytics for optimal community management.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Ecosystem Vision Section */}
      <section className="py-20 bg-slate-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-6 text-slate-900">
              Beyond Community Management:
              <span className="block text-blue-600">A Connected Ecosystem</span>
            </h2>
            <p className="text-xl text-slate-600 leading-relaxed">
              We're not just another community app. We're building the foundation for a
              <strong> grassroots movement of interconnectivity</strong> that transforms how
              communities, citizens, and municipalities work together.
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h3 className="text-3xl font-bold mb-6 text-slate-900">The Ecosystem Advantage</h3>
              <div className="space-y-6">
                <div className="flex items-start space-x-4">
                  <div className="h-10 w-10 bg-green-100 rounded-lg flex items-center justify-center shrink-0">
                    <Home className="h-5 w-5 text-green-600" />
                  </div>
                  <div>
                    <h4 className="text-lg font-semibold text-slate-900 mb-2">
                      Home-to-Land Connection
                    </h4>
                    <p className="text-slate-600">
                      Direct integration between individual homes, property data, and land
                      management systems.
                    </p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="h-10 w-10 bg-blue-100 rounded-lg flex items-center justify-center shrink-0">
                    <Users className="h-5 w-5 text-blue-600" />
                  </div>
                  <div>
                    <h4 className="text-lg font-semibold text-slate-900 mb-2">
                      Citizen-to-Community Bridge
                    </h4>
                    <p className="text-slate-600">
                      Seamless connection between individual residents and community governance,
                      services, and activities.
                    </p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="h-10 w-10 bg-purple-100 rounded-lg flex items-center justify-center shrink-0">
                    <MapPin className="h-5 w-5 text-purple-600" />
                  </div>
                  <div>
                    <h4 className="text-lg font-semibold text-slate-900 mb-2">
                      Municipal Integration
                    </h4>
                    <p className="text-slate-600">
                      Direct connection to city systems, enabling unprecedented coordination between
                      communities and local government.
                    </p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="h-10 w-10 bg-indigo-100 rounded-lg flex items-center justify-center shrink-0">
                    <Globe className="h-5 w-5 text-indigo-600" />
                  </div>
                  <div>
                    <h4 className="text-lg font-semibold text-slate-900 mb-2">
                      Global Network Effect
                    </h4>
                    <p className="text-slate-600">
                      As more communities join, the network becomes exponentially more valuable
                      through shared resources and knowledge.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="relative">
              <div className="bg-linear-to-br from-blue-600 to-indigo-600 rounded-2xl p-8 text-white">
                <h4 className="text-2xl font-bold mb-4">The Multiplication Effect</h4>
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <span>Isolated Community</span>
                    <span className="font-bold">1x Impact</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span>Connected Community</span>
                    <span className="font-bold text-yellow-300">10x Impact</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span>Network of Communities</span>
                    <span className="font-bold text-yellow-300">100x Impact</span>
                  </div>
                  <div className="flex items-center justify-between border-t border-white/20 pt-4">
                    <span className="text-lg">Global Ecosystem</span>
                    <span className="text-2xl font-bold text-yellow-300">∞ Impact</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Competitive Advantage Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-6 text-slate-900">
              Why Choose Us Over Everyone Else?
            </h2>
            <p className="text-xl text-slate-600 leading-relaxed">
              While others build isolated tools, we're creating the{' '}
              <strong>global infrastructure</strong>
              for connected communities. Here's what sets us apart:
            </p>
          </div>

          <div className="grid lg:grid-cols-3 gap-8">
            <Card className="border-green-200 bg-green-50/50 relative overflow-hidden">
              <div className="absolute top-4 right-4">
                <Badge className="bg-green-600 text-white">
                  <Award className="h-3 w-3 mr-1" />
                  Best-in-Class
                </Badge>
              </div>
              <CardHeader>
                <CardTitle className="text-green-900 text-xl">vs. Custom Solutions</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex items-center space-x-2">
                  <CheckCircle className="h-5 w-5 text-green-600" />
                  <span className="text-green-800">Enterprise-grade from day one</span>
                </div>
                <div className="flex items-center space-x-2">
                  <CheckCircle className="h-5 w-5 text-green-600" />
                  <span className="text-green-800">Global network connectivity</span>
                </div>
                <div className="flex items-center space-x-2">
                  <CheckCircle className="h-5 w-5 text-green-600" />
                  <span className="text-green-800">Continuous updates & support</span>
                </div>
                <div className="flex items-center space-x-2">
                  <CheckCircle className="h-5 w-5 text-green-600" />
                  <span className="text-green-800">Lower total cost of ownership</span>
                </div>
              </CardContent>
            </Card>

            <Card className="border-blue-200 bg-blue-50/50 relative overflow-hidden">
              <div className="absolute top-4 right-4">
                <Badge className="bg-blue-600 text-white">
                  <Star className="h-3 w-3 mr-1" />
                  Superior
                </Badge>
              </div>
              <CardHeader>
                <CardTitle className="text-blue-900 text-xl">vs. Point Solutions</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex items-center space-x-2">
                  <CheckCircle className="h-5 w-5 text-blue-600" />
                  <span className="text-blue-800">Unified platform eliminates silos</span>
                </div>
                <div className="flex items-center space-x-2">
                  <CheckCircle className="h-5 w-5 text-blue-600" />
                  <span className="text-blue-800">Single data source of truth</span>
                </div>
                <div className="flex items-center space-x-2">
                  <CheckCircle className="h-5 w-5 text-blue-600" />
                  <span className="text-blue-800">Integrated workflows</span>
                </div>
                <div className="flex items-center space-x-2">
                  <CheckCircle className="h-5 w-5 text-blue-600" />
                  <span className="text-blue-800">Massive cost savings</span>
                </div>
              </CardContent>
            </Card>

            <Card className="border-purple-200 bg-purple-50/50 relative overflow-hidden">
              <div className="absolute top-4 right-4">
                <Badge className="bg-purple-600 text-white">
                  <Crown className="h-3 w-3 mr-1" />
                  Revolutionary
                </Badge>
              </div>
              <CardHeader>
                <CardTitle className="text-purple-900 text-xl">vs. Legacy Systems</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex items-center space-x-2">
                  <CheckCircle className="h-5 w-5 text-purple-600" />
                  <span className="text-purple-800">Modern, intuitive interface</span>
                </div>
                <div className="flex items-center space-x-2">
                  <CheckCircle className="h-5 w-5 text-purple-600" />
                  <span className="text-purple-800">Cloud-native architecture</span>
                </div>
                <div className="flex items-center space-x-2">
                  <CheckCircle className="h-5 w-5 text-purple-600" />
                  <span className="text-purple-800">Mobile-first design</span>
                </div>
                <div className="flex items-center space-x-2">
                  <CheckCircle className="h-5 w-5 text-purple-600" />
                  <span className="text-purple-800">Future-proof technology</span>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* ROI Section */}
      <section className="py-20 bg-linear-to-br from-green-600 to-emerald-700 text-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              Immediate ROI, Exponential Growth
            </h2>
            <p className="text-xl text-green-100 leading-relaxed">
              Our customers see immediate returns and exponential value growth as they tap into the
              global community network.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <Card className="bg-white/10 border-white/20 text-white">
              <CardHeader className="text-center">
                <div className="text-4xl font-bold text-yellow-300 mb-2">Month 1</div>
                <CardTitle>Immediate Impact</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex items-center space-x-2">
                  <CheckCircle className="h-5 w-5 text-green-300" />
                  <span>50% reduction in admin time</span>
                </div>
                <div className="flex items-center space-x-2">
                  <CheckCircle className="h-5 w-5 text-green-300" />
                  <span>Unified communication system</span>
                </div>
                <div className="flex items-center space-x-2">
                  <CheckCircle className="h-5 w-5 text-green-300" />
                  <span>Digital financial management</span>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-white/10 border-white/20 text-white">
              <CardHeader className="text-center">
                <div className="text-4xl font-bold text-yellow-300 mb-2">Month 6</div>
                <CardTitle>Accelerated Benefits</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex items-center space-x-2">
                  <CheckCircle className="h-5 w-5 text-green-300" />
                  <span>Network effect multipliers</span>
                </div>
                <div className="flex items-center space-x-2">
                  <CheckCircle className="h-5 w-5 text-green-300" />
                  <span>Bulk purchasing savings</span>
                </div>
                <div className="flex items-center space-x-2">
                  <CheckCircle className="h-5 w-5 text-green-300" />
                  <span>Collaborative problem-solving</span>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-white/10 border-white/20 text-white">
              <CardHeader className="text-center">
                <div className="text-4xl font-bold text-yellow-300 mb-2">Year 1+</div>
                <CardTitle>Exponential Growth</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex items-center space-x-2">
                  <CheckCircle className="h-5 w-5 text-green-300" />
                  <span>Global network access</span>
                </div>
                <div className="flex items-center space-x-2">
                  <CheckCircle className="h-5 w-5 text-green-300" />
                  <span>AI-powered insights</span>
                </div>
                <div className="flex items-center space-x-2">
                  <CheckCircle className="h-5 w-5 text-green-300" />
                  <span>Infinite scalability</span>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-slate-900 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Ready to Transform Your Community?
          </h2>
          <p className="text-xl text-slate-300 mb-12 max-w-3xl mx-auto">
            Join the global network of connected communities. Experience the power of true ecosystem
            integration and watch your community thrive like never before.
          </p>

          <div className="flex flex-col sm:flex-row gap-6 justify-center items-center mb-12">
            <Link to="/onboarding">
              <Button
                size="lg"
                className="bg-linear-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-lg px-8 py-4"
              >
                <Rocket className="h-5 w-5 mr-2" />
                Start Your Transformation Now
              </Button>
            </Link>
            <Link to="/pricing">
              <Button
                variant="outline"
                size="lg"
                className="text-lg px-8 py-4 border-white/30 text-white hover:bg-white hover:text-slate-900"
              >
                View Pricing & Plans
                <ChevronRight className="h-5 w-5 ml-2" />
              </Button>
            </Link>
          </div>

          <div className="flex justify-center space-x-8">
            <Link
              to="/pricing"
              className="text-slate-400 hover:text-white transition-colors flex items-center"
            >
              <TrendingUp className="h-5 w-5 mr-2" />
              Pricing
            </Link>
            <Link
              to="/licensing"
              className="text-slate-400 hover:text-white transition-colors flex items-center"
            >
              <Shield className="h-5 w-5 mr-2" />
              Licensing
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default SalesPage;
